import { useMemo } from "react";

import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import { Application } from "@core/Application";
import { NovedadesExportService } from "@services/NovedadesExportService";
import { IconButton, NormalButton, RefreshButton } from "@shared/designSystem/Button";
import {
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer
} from "@shared/designSystem/Containers";
import SearchBar from "@shared/designSystem/SearchBar";
import { SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";

import {
    ExportNovedadesListItem,
    useExportNovedadesScreenViewModel,
} from "./ExportNovedadesScreenViewModel";
import ExportNovedadesConfigurationModalScreen from "./ExportNovedadesConfigurationModalScreen";
import ExportNovedadesModalScreen from "./ExportNovedadesModalScreen";

interface ExportNovedadesScreenProps {
    orgID: string;
    timezone?: string;
}

const columnWidths = {
    title: "26%",
    period: "16%",
    status: "14%",
    progress: "14%",
    totals: "16%",
    updatedAt: "14%",
};

const ExportRow = ({
    row,
    onClick,
}: {
    row: ExportNovedadesListItem;
    onClick: () => void;
}) => {
    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onClick();
                }
            }}
            style={{ width: "100%", cursor: "pointer" }}
        >
            <TableRowContainer>
                <HorizontalContainer width={columnWidths.title} minWidth="0">
                    <VerticalContainer gap={designSystemStyles.gapNone} width="100%" minWidth="0">
                        <Text text={row.title} truncate={true} />
                        <SmallText
                            text={row.fileName}
                            color={designSystemStyles.colorTextSecondary}
                            truncate={true}
                        />
                    </VerticalContainer>
                </HorizontalContainer>
                <TableCell text={row.period} width={columnWidths.period} truncate={true} />
                <TableCell text={row.status} width={columnWidths.status} justifyContent="center" truncate={true} />
                <TableCell text={row.progress} width={columnWidths.progress} justifyContent="center" truncate={true} />
                <TableCell text={row.totals} width={columnWidths.totals} justifyContent="center" truncate={true} />
                <TableCell text={row.updatedAt} width={columnWidths.updatedAt} justifyContent="right" truncate={true} />
            </TableRowContainer>
        </div>
    );
};

const ExportNovedadesScreen: React.FC<ExportNovedadesScreenProps> = ({ orgID, timezone }) => {
    const service = useMemo(() => {
        return new NovedadesExportService(Application.shared().coreDependencies.httpClient);
    }, []);
    const usersService = useMemo(() => {
        return Application.shared().coreDependencies.usersService;
    }, []);

    const viewModel = useExportNovedadesScreenViewModel({
        orgID,
        timezone,
        service,
    });

    const emptyText = viewModel.rows.length === 0
        ? "No hay exportaciones de novedades para mostrar.".localized()
        : "No hay exportaciones que coincidan con la búsqueda.".localized();
    const headerHeight = designSystemStyles.sizeRowMedium;
    const handleManualRefresh = async () => {
        const refreshedProcesses = await viewModel.refresh();
        NovedadesExportAnalyticsHelper.trackListRefreshed(refreshedProcesses.length);
    };

    return (
        <VerticalContainer
            width="100%"
            height="100%"
            gap={designSystemStyles.gapMd}
            margin={designSystemStyles.paddingZero}
        >
            <HorizontalContainer
                width="100%"
                gap={designSystemStyles.gapMd}
                paddingBottom={designSystemStyles.paddingSm}
            >
                <HorizontalContainer>
                    <SearchBar
                        placeholder={"Buscar exportación".localized()}
                        value={viewModel.searchField}
                        onChange={viewModel.setSearchField}
                    />

                    <RefreshButton
                        onClick={() => void handleManualRefresh()}
                    />
                </HorizontalContainer>
                <HorizontalContainer
                    justifyContent="flex-end"
                    gap={designSystemStyles.gapMd}
                >
                    <IconButton
                        iconType="settings"
                        tooltip={"Configurar exportación".localized()}
                        onClick={viewModel.openConfigurationModal}
                    />
                    <NormalButton
                        text={"Nueva exportación".localized()}
                        onClick={viewModel.openCreateExport}
                        width="220px"
                    />

                </HorizontalContainer>
            </HorizontalContainer>

            <VerticalContainer
                width="100%"
                height="100%"
                margin={designSystemStyles.paddingZero}
                scrollable={true}
            >
                {viewModel.loading ? (
                    <QontyLoading
                        text={"Cargando exportaciones...".localized()}
                        withQonty={true}
                    />
                ) : viewModel.error ? (
                    <QontyEmptyState text={viewModel.error.localized()} darkMode={false} />
                ) : viewModel.filteredRows.length === 0 ? (
                    <QontyEmptyState text={emptyText} darkMode={false} />
                ) : (
                    <VerticalContainer
                        width="100%"
                        height="100%"
                        margin={designSystemStyles.paddingZero}
                        gap={designSystemStyles.gapMd}
                    >
                        <TableHeaderContainer height={headerHeight}>
                            <TableHeaderCell text={"Exportación".localized()} width={columnWidths.title} truncate={true} />
                            <TableHeaderCell text={"Período".localized()} width={columnWidths.period} truncate={true} />
                            <TableHeaderCell text={"Estado".localized()} width={columnWidths.status} justifyContent="center" truncate={true} />
                            <TableHeaderCell text={"Progreso".localized()} width={columnWidths.progress} justifyContent="center" truncate={true} />
                            <TableHeaderCell text={"Resultado".localized()} width={columnWidths.totals} justifyContent="center" truncate={true} />
                            <TableHeaderCell text={"Actualización".localized()} width={columnWidths.updatedAt} justifyContent="right" truncate={true} />
                        </TableHeaderContainer>
                        <VerticalContainer
                            width="100%"
                            margin={designSystemStyles.paddingZero}
                            gap={designSystemStyles.gapNone}
                            scrollable={true}
                        >
                            {viewModel.filteredRows.map((row) => (
                                <ExportRow
                                    key={row.id}
                                    row={row}
                                    onClick={() => viewModel.openProcess(row.raw)}
                                />
                            ))}
                        </VerticalContainer>
                    </VerticalContainer>
                )}
            </VerticalContainer>
            <ExportNovedadesModalScreen
                isOpen={viewModel.isModalOpen}
                mode={viewModel.modalMode}
                orgID={orgID}
                service={service}
                usersService={usersService}
                process={viewModel.selectedProcess}
                onClose={viewModel.closeModal}
                onCreated={viewModel.handleProcessCreated}
                onProcessUpdated={async () => {
                    await viewModel.refresh();
                }}
            />
            <ExportNovedadesConfigurationModalScreen
                isOpen={viewModel.isConfigurationModalOpen}
                orgID={orgID}
                onClose={viewModel.closeConfigurationModal}
            />
        </VerticalContainer>
    );
};

export default ExportNovedadesScreen;
